import { Box } from 'components/boxs'
import { RadioButtonGroup } from 'components/buttons'
import { DropdownFull } from 'components/dropdown'
import { Icon } from 'components/icon'
import { Input } from 'components/input'
import { ModalConfirm } from 'components/modal'
import { Typography } from 'components/typography'
import {
	DropdownFullItemProps,
	Icons,
	OrganizationInformationForm,
	TypographyTypes
} from 'interfaces'
import { allSettingsTimeTypesData } from 'lib'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { regexUrl } from 'utils'

interface SettingsSSOProps {
	time: DropdownFullItemProps
	setTime: Dispatch<SetStateAction<DropdownFullItemProps | undefined>>
}

export const SettingsSSO: React.FC<SettingsSSOProps> = props => {
	const { time, setTime } = props
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isModalOpenToken, setIsModalOpenToken] = useState(false)

	const { register, setValue, formState, control, watch } =
		useFormContext<OrganizationInformationForm>()
	const { errors } = formState

	const valueSsoLin = watch('sso.sso_link')
	const valuePassRecovery = watch('sso.password_recovery_link_sso')
	const handleDelete = (): void => {
		setValue('sso.token_sso', '')
		setValue('sso.sso_link', '')
		setValue('sso.data_refresh_frecuency_sso', undefined)
		setTime(undefined)
		setIsModalOpen(false)
	}

	const externalPassOptions = [
		{
			id: 0,
			value: 'disabled',
			label: 'Disabled'
		},
		{
			id: 1,
			value: 'enabled',
			label: 'Enabled'
		}
	]

	const [radioButtonOption, setRadioButtonOption] = useState(
		externalPassOptions[0].value
	)
	const rules = {
		sso_link: {
			pattern: {
				value: regexUrl,
				message: 'Invalid link'
			}
		},
		data_refresh_frecuency_sso: {
			required: {
				value: valueSsoLin?.length > 1,
				message: 'Please select an option'
			}
		},
		email_support_sso: {
			maxLength: { value: 150, message: 'Maximum characters exceeded' }
		},
		token_sso: {
			required: {
				value: valueSsoLin?.length > 1,
				message: 'This field is required'
			}
		},
		password_recovery_link_sso: {
			pattern: {
				value: regexUrl,
				message: 'Invalid link'
			},
			required: {
				value: radioButtonOption === 'enabled',
				message: 'This field is required'
			}
		}
	}

	useEffect(() => {
		if (
			valuePassRecovery &&
			valuePassRecovery?.length > 1 &&
			radioButtonOption === 'disabled'
		) {
			setRadioButtonOption('enabled')
		}
	}, [valuePassRecovery])

	useEffect(() => {
		if (
			radioButtonOption === 'disabled' &&
			valuePassRecovery &&
			valuePassRecovery?.length > 1
		) {
			setRadioButtonOption('disabled')
			setValue('sso.password_recovery_link_sso', null)
		}
	}, [radioButtonOption])

	return (
		<>
			<Box className="border-none min-h-[350px]">
				<div>
					<div className="flex flex-row items-center pb-4 gap-2">
						<Icon src={Icons.config} className="w-5 h-5 text-blue-primary" />
						<Typography
							title="SSO connection:"
							fontLeading="15/18"
							type={TypographyTypes.h3}
							className="text-black-1"
						/>
					</div>

					<div className="border-blue-primary rounded-2xl border-2  ">
						<div className="flex flex-col md:flex-row gap-2">
							<div className="p-4 md:w-1/2 w-full ">
								<Input
									title="Organization SSO link:"
									placeholder="Paste LINK here"
									name="sso.sso_link"
									onChange={e => {
										setValue('sso.sso_link', e.target.value)
									}}
									type="text"
									register={register}
									rules={rules.sso_link}
									error={errors.sso?.sso_link}
									rightIcon={Icons.delete}
									fillpat
									rightClick={() => {
										setIsModalOpen(true)
									}}
								/>
								<Input
									title="Token:"
									placeholder="Paste token"
									name="sso.token_sso"
									onChange={e => {
										setValue('sso.token_sso', e.target.value)
									}}
									type="text"
									register={register}
									rules={rules.token_sso}
									error={errors.sso?.token_sso}
									className="py-3"
									rightIcon={Icons.delete}
									fillpat
									rightClick={() => {
										setIsModalOpenToken(true)
									}}
								/>
							</div>

							<div className="p-4 md:w-1/2 w-full ">
								<div className="pb-3">
									<Typography
										title="Data refresh frecuency"
										fontLeading="12/12"
										type={TypographyTypes.h3}
										className="text-black-1 pb-8"
									/>
									<Controller
										name="sso.data_refresh_frecuency_sso"
										control={control}
										rules={rules.data_refresh_frecuency_sso}
										render={() => (
											<DropdownFull
												placeholder="Set timeframe"
												item={time as DropdownFullItemProps}
												list={allSettingsTimeTypesData}
												setItem={setTime}
												icon={Icons.arrowDownFull}
												className="min-w-[180px]"
												iconClass="w-[10px] text-blue-primary"
												error={errors.sso?.data_refresh_frecuency_sso}
											/>
										)}
									/>
								</div>

								<Input
									title="Set Suppport email:"
									placeholder="Default is support@patter.com"
									type="email"
									name="sso.email_support_sso"
									register={register}
									rules={rules.email_support_sso}
								/>
								<Typography
									title="Please note that by default email support@patter.com will be used for support. We recommend editing the email if you have a different one."
									fontLeading="12/12"
									type={TypographyTypes.h3}
									className="text-gray-3 pt-3"
								/>
							</div>
						</div>

						<div className="p-4 md:w w-full ">
							<div className="pb-3">
								<Typography
									title="External Password Recovery Option"
									fontLeading="12/12"
									type={TypographyTypes.h3}
									className="text-black-1"
								/>
							</div>

							<div className="pb-4">
								<RadioButtonGroup
									items={externalPassOptions}
									radioButtonOption={radioButtonOption}
									setRadioButtonOption={setRadioButtonOption}
									value={radioButtonOption}
								/>
							</div>

							{radioButtonOption === 'enabled' && (
								<div>
									<Input
										title="Set Password Recovery Link:"
										placeholder="https://www.linkexample.com"
										type="url"
										name="sso.password_recovery_link_sso"
										register={register}
										rules={rules.password_recovery_link_sso}
										error={errors.sso?.password_recovery_link_sso}
										onChange={e => {
											setValue('sso.password_recovery_link_sso', e.target.value)
										}}
										className="w-1/2"
									/>
									<Typography
										// eslint-disable-next-line max-len
										title="Provide a link that directs users to a password recovery page, FAQ, or another resource where they can find the information needed to reset their password."
										fontLeading="12/12"
										type={TypographyTypes.h3}
										className="text-gray-3 pt-3"
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</Box>
			<ModalConfirm
				open={isModalOpen}
				setOpen={setIsModalOpen}
				onClickConfirm={() => {
					handleDelete()
				}}
				icon={Icons.warning}
				descripcion="Are you sure you want to delete the LINK"
				buttonLabel="Confirm"
			/>
			<ModalConfirm
				open={isModalOpenToken}
				setOpen={setIsModalOpenToken}
				onClickConfirm={() => {
					setValue('sso.token_sso', '')
					setIsModalOpenToken(false)
				}}
				icon={Icons.warning}
				descripcion="Are you sure you want to delete the Token"
				buttonLabel="Confirm"
			/>
		</>
	)
}
